import { useEffect, useState } from 'react';
import type { CalendarDateTime } from '@internationalized/date';
import {
  Button,
  Description,
  Drawer,
  Separator,
  type OverlayTriggerState,
} from '@heroui/react';
import type {
  AvailabilityWindow,
  CreateAvailabilityWindowRequest,
  ParkingSpotOwner,
  RecurrenceFrequency,
  UpdateAvailabilityWindowRequest,
} from '../../../api/my-parking-spots';
import { AsyncButton } from '../../../components/ui/async-button';
import { InlineDateTimeSection } from '../../../components/ui/inline-datetime-section';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { UsageTypeCheckboxGroup } from '../../../components/ui/usage-type-checkbox-group';
import { getErrorMessage } from '../../../lib/toast';
import { useRecurringAvailabilityEnabled } from '../../../hooks/useFeatureFlags';
import {
  AvailabilityDurationPresets,
  type AvailabilityDurationPresetId,
} from './availability-duration-presets';
import { RecurrenceSection } from './recurrence-section';
import {
  buildOverlapWindowErrorMessage,
  findOverlappingWindow,
  isOverlapWindowError,
  parseOverlapWindowPeriod,
} from '../utils/availability-windows';
import {
  addHoursToCalendarDateTime,
  addMinutesToCalendarDateTime,
  calendarDateTimeToIsoUtc,
  compareCalendarDateTime,
  getCalendarEndWhenStartChanges,
  getCurrentCalendarDateTime,
  isoUtcToCalendarDateTime,
} from '../../../utils/date-utils';
import { recurrenceOverlapsSelf } from '../utils/recurrence';

interface WindowFormData {
  startAt: CalendarDateTime | null;
  endAt: CalendarDateTime | null;
  allowBorrow: boolean;
  allowRent: boolean;
}

interface AvailabilityWindowDrawerProps {
  state: OverlayTriggerState;
  spot: ParkingSpotOwner | null;
  activeWindows: AvailabilityWindow[];
  editingWindow: AvailabilityWindow | null;
  onSubmit: (
    payload: CreateAvailabilityWindowRequest | { windowId: string; payload: UpdateAvailabilityWindowRequest },
  ) => Promise<void>;
}

function defaultFormData(): WindowFormData {
  const startAt = addMinutesToCalendarDateTime(getCurrentCalendarDateTime(), 5);
  return {
    startAt,
    endAt: addHoursToCalendarDateTime(startAt, 1),
    allowBorrow: true,
    allowRent: false,
  };
}

function formDataFromWindow(window: AvailabilityWindow): WindowFormData {
  return {
    startAt: window.startAt ? isoUtcToCalendarDateTime(window.startAt) : null,
    endAt: window.endAt ? isoUtcToCalendarDateTime(window.endAt) : null,
    allowBorrow: window.allowBorrow,
    allowRent: window.allowRent,
  };
}

function OverlapWindowErrorDescription({ message }: { message: string }) {
  const period = parseOverlapWindowPeriod(message);
  if (!period) return message;

  return (
    <>
      Há uma janela de disponibilidade aberta de <strong>{period.start}</strong> até{' '}
      <strong>{period.end}</strong>
    </>
  );
}

export function AvailabilityWindowDrawer({
  state,
  spot,
  activeWindows,
  editingWindow,
  onSubmit,
}: AvailabilityWindowDrawerProps) {
  const [form, setForm] = useState<WindowFormData>(defaultFormData);
  const [selectedPresetId, setSelectedPresetId] = useState<AvailabilityDurationPresetId | null>(
    '1h',
  );
  const recurrenceEnabled = useRecurringAvailabilityEnabled();
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState<RecurrenceFrequency>('WEEKLY');
  const [repeatOccurrences, setRepeatOccurrences] = useState(8);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const repeating = recurrenceEnabled && repeatEnabled;

  useEffect(() => {
    if (!state.isOpen) return;

    setForm(editingWindow ? formDataFromWindow(editingWindow) : defaultFormData());
    setSelectedPresetId(editingWindow ? null : '1h');
    setRepeatEnabled(false);
    setRepeatFrequency('WEEKLY');
    setRepeatOccurrences(8);
    setError(null);
    setSubmitting(false);
  }, [state.isOpen, editingWindow]);

  const title = editingWindow ? 'Editar janela de disponibilidade' : 'Nova janela de disponibilidade';

  const handleResetValues = () => {
    setForm(editingWindow ? formDataFromWindow(editingWindow) : defaultFormData());
    setSelectedPresetId(editingWindow ? null : '1h');
    setRepeatEnabled(false);
    setError(null);
  };

  const handleManualPeriodChange = (patch: Partial<WindowFormData>) => {
    setSelectedPresetId(null);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSubmit = async () => {
    if (!spot) return;

    if (!form.startAt || !form.endAt) {
      setError('Data de início e fim são obrigatórias.');
      return;
    }

    const now = getCurrentCalendarDateTime();
    const minStart = addMinutesToCalendarDateTime(now, 1);
    if (compareCalendarDateTime(form.startAt, minStart) < 0) {
      setError('A data e hora de início deve ser pelo menos 1 minuto no futuro.');
      return;
    }

    const minEnd = addMinutesToCalendarDateTime(form.startAt, 1);
    if (compareCalendarDateTime(form.endAt, minEnd) < 0) {
      setError('A data e hora de fim deve ser pelo menos 1 minuto depois da data de início.');
      return;
    }

    const startAtISO = calendarDateTimeToIsoUtc(form.startAt);
    const endAtISO = calendarDateTimeToIsoUtc(form.endAt);

    const overlappingWindow = findOverlappingWindow(
      activeWindows,
      startAtISO,
      endAtISO,
      editingWindow?.id,
    );
    if (overlappingWindow) {
      setError(buildOverlapWindowErrorMessage(overlappingWindow));
      return;
    }

    const shouldRepeat = !editingWindow && repeating;
    if (shouldRepeat && recurrenceOverlapsSelf(startAtISO, endAtISO, repeatFrequency)) {
      setError(
        repeatFrequency === 'DAILY'
          ? 'Esse intervalo é longo demais para repetir todos os dias sem se sobrepor.'
          : 'Esse intervalo é longo demais para repetir toda semana sem se sobrepor.',
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (editingWindow) {
        await onSubmit({
          windowId: editingWindow.id,
          payload: {
            startAt: startAtISO,
            endAt: endAtISO,
            allowBorrow: form.allowBorrow,
            allowRent: form.allowRent,
          },
        });
      } else {
        await onSubmit({
          spotId: spot.spotId,
          startAt: startAtISO,
          endAt: endAtISO,
          allowBorrow: form.allowBorrow,
          allowRent: form.allowRent,
          recurrence: shouldRepeat
            ? { frequency: repeatFrequency, occurrences: repeatOccurrences }
            : undefined,
        });
      }
      state.close();
    } catch (err) {
      setError(
        getErrorMessage(err, 'Erro ao salvar janela de disponibilidade. Tente novamente.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{title}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              {spot ? (
                <Description>
                  Vaga: <strong>{spot.spotCode}</strong>
                </Description>
              ) : null}

              {error ? (
                <StatusCallout
                  status="danger"
                  title={
                    isOverlapWindowError(error) ? 'Janela já existente' : 'Não foi possível salvar'
                  }
                  action={
                    <Button size="sm" variant="danger-soft" onPress={handleResetValues}>
                      Limpar valores
                    </Button>
                  }
                >
                  {isOverlapWindowError(error) ? (
                    <OverlapWindowErrorDescription message={error} />
                  ) : (
                    error
                  )}
                </StatusCallout>
              ) : null}

              {!editingWindow ? (
                <>
                  <AvailabilityDurationPresets
                    selectedPresetId={selectedPresetId}
                    startAt={form.startAt}
                    endAt={form.endAt}
                    activeWindows={activeWindows}
                    onSelectPreset={(presetId, startAt, endAt) => {
                      setSelectedPresetId(presetId);
                      setForm((prev) => ({ ...prev, startAt, endAt }));
                      setError(null);
                    }}
                  />

                  <div className="flex items-center gap-3">
                    <Separator className="flex-1" />
                    <span className="text-xs uppercase tracking-wide text-muted">ou</span>
                    <Separator className="flex-1" />
                  </div>

                  <Description className="text-xs text-muted">
                    Escolha início e fim manualmente nos calendários abaixo.
                  </Description>
                </>
              ) : null}

              <div className="space-y-6">
                <InlineDateTimeSection
                  title="Data e hora de início"
                  value={form.startAt}
                  minValue={getCurrentCalendarDateTime()}
                  markedWindows={activeWindows}
                  unavailableExcludeWindowId={editingWindow?.id}
                  fieldMode="start"
                  onChange={(startAt) =>
                    handleManualPeriodChange({
                      startAt,
                      endAt: getCalendarEndWhenStartChanges(startAt, form.endAt),
                    })
                  }
                />

                <InlineDateTimeSection
                  title="Data e hora de fim"
                  value={form.endAt}
                  minValue={
                    form.startAt
                      ? addMinutesToCalendarDateTime(form.startAt, 1)
                      : getCurrentCalendarDateTime()
                  }
                  markedWindows={activeWindows}
                  unavailableExcludeWindowId={editingWindow?.id}
                  fieldMode="end"
                  startAtReference={form.startAt}
                  onChange={(endAt) => handleManualPeriodChange({ endAt })}
                />
              </div>

              {activeWindows.length > 0 ? (
                <p className="text-xs text-muted">
                  O ponto abaixo do dia indica janela ativa. Dias parcialmente ocupados continuam
                  selecionáveis; a hora mínima ou máxima é ajustada conforme as janelas existentes.
                </p>
              ) : null}

              <div className={activeWindows.length > 0 ? 'mt-6' : undefined}>
                <UsageTypeCheckboxGroup
                  inCard={false}
                  requireAtLeastOne
                  label="Tipo de uso permitido"
                  description="Marque pelo menos um tipo de uso para a janela."
                  borrowLabel="Permitir empréstimo"
                  rentLabel="Permitir aluguel"
                  borrowSelected={form.allowBorrow}
                  rentSelected={form.allowRent}
                  onChange={(allowBorrow, allowRent) =>
                    setForm((prev) => ({ ...prev, allowBorrow, allowRent }))
                  }
                />
              </div>

              {recurrenceEnabled && !editingWindow && form.startAt && form.endAt ? (
                <RecurrenceSection
                  enabled={repeatEnabled}
                  frequency={repeatFrequency}
                  occurrences={repeatOccurrences}
                  startAtIso={calendarDateTimeToIsoUtc(form.startAt)}
                  endAtIso={calendarDateTimeToIsoUtc(form.endAt)}
                  overlapError={
                    repeatEnabled &&
                    recurrenceOverlapsSelf(
                      calendarDateTimeToIsoUtc(form.startAt),
                      calendarDateTimeToIsoUtc(form.endAt),
                      repeatFrequency,
                    )
                      ? repeatFrequency === 'DAILY'
                        ? 'Esse intervalo é longo demais para repetir todos os dias.'
                        : 'Esse intervalo é longo demais para repetir toda semana.'
                      : null
                  }
                  onEnabledChange={setRepeatEnabled}
                  onFrequencyChange={(frequency) => {
                    setRepeatFrequency(frequency);
                    setRepeatOccurrences(frequency === 'DAILY' ? 7 : 8);
                  }}
                  onOccurrencesChange={setRepeatOccurrences}
                />
              ) : null}
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="secondary" onPress={() => state.close()}>
                Fechar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!form.startAt || !form.endAt}
                pendingLabel={
                  editingWindow
                    ? 'Salvar alterações'
                    : repeating
                      ? 'Criar janelas'
                      : 'Criar janela'
                }
                onPress={() => void handleSubmit()}
              >
                {editingWindow
                  ? 'Salvar alterações'
                  : repeating
                    ? `Criar ${repeatOccurrences} janelas`
                    : 'Criar janela'}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
